// ABOUTME: Maps each known job type to the overview sections and counter cards it renders.
// ABOUTME: Unknown and legacy job types fall back to a generic presentation with no node counters.

import { CounterType, DetailType } from '../types';
import { JobCounterCard, JobPresentation, JobPresentationContext, JobSectionId } from './types';

/**
 * Errors are relevant to every job, so this card is appended to whatever counters a job type
 * declares instead of being repeated in each preset.
 */
export const FAILURES_CARD: JobCounterCard = {
    detailType: DetailType.FAILURES,
    counterType: CounterType.ERROR,
};

// Sections shared by every job. A job type we know nothing about still gets 'counters',
// but with the errors card alone: it must never claim it created or modified nodes.
const DEFAULT_SECTIONS: JobSectionId[] = ['counters', 'files'];
// Maintenance and in-place jobs never produce a downloadable file.
const NO_FILES: JobSectionId[] = ['counters'];

const priceListCards: JobCounterCard[] = [
    { detailType: DetailType.CREATED, counterType: CounterType.SUCCESS },
    { detailType: DetailType.PRICING_CHANGES, counterType: CounterType.SUCCESS },
    { detailType: DetailType.NON_RELEVANT_CHANGES, counterType: CounterType.SUCCESS },
    { detailType: DetailType.NON_MODIFIED, counterType: CounterType.INFO, disableDetail: true },
    { detailType: DetailType.DISABLED, counterType: CounterType.INFO },
    { detailType: DetailType.UPDATED_GLS, counterType: CounterType.UPDATED },
    { detailType: DetailType.ALIGNED_MODELS, counterType: CounterType.UPDATED },
    { detailType: DetailType.ALIGNED_GLS, counterType: CounterType.UPDATED },
];

const simpleImportCards: JobCounterCard[] = [
    { detailType: DetailType.CREATED_MODIFIED, counterType: CounterType.SUCCESS },
    { detailType: DetailType.NON_MODIFIED, counterType: CounterType.INFO },
];

const isCgtPriceListImport = (ctx: JobPresentationContext) =>
    !!ctx.priceListHierarchyId && ctx.aggregateId === ctx.priceListHierarchyId;

const sumCounters = (ctx: JobPresentationContext, types: DetailType[]) =>
    types.reduce((total, type) => total + (ctx.counters[type] ?? 0), 0);

/** The CAT import recap: the manual CatImport and the scheduled cat-import (one run per company). */
const CAT_IMPORT_PRESENTATION: Omit<JobPresentation, 'id'> = {
    category: 'import',
    sections: DEFAULT_SECTIONS,
    // The CAT import declares thirteen cards; grouping them by colour keeps the greens,
    // the ambers and the neutrals on rows of their own.
    groupCountersByColor: true,
    counters: () => [
        { detailType: DetailType.CREATED_FAMILIES, counterType: CounterType.SUCCESS },
        { detailType: DetailType.CREATED_MODELS, counterType: CounterType.SUCCESS },
        ...priceListCards,
        { detailType: DetailType.BLOCKED_PRICE_UPDATE, counterType: CounterType.WARNING },
        {
            detailType: DetailType.BLOCKED_NOT_CHARGED_UPDATE,
            counterType: CounterType.WARNING,
        },
    ],
    recordCount: ctx =>
        sumCounters(ctx, [
            DetailType.CREATED,
            DetailType.PRICING_CHANGES,
            DetailType.NON_RELEVANT_CHANGES,
            DetailType.NON_MODIFIED,
            DetailType.DISABLED,
        ]),
};

export const JOB_PRESENTATIONS: Record<string, JobPresentation> = {
    // ---- Import ----------------------------------------------------------------
    import: {
        id: 'import',
        category: 'import',
        sections: DEFAULT_SECTIONS,
        counters: ctx => (isCgtPriceListImport(ctx) ? priceListCards : simpleImportCards),
        recordCount: ctx =>
            isCgtPriceListImport(ctx)
                ? sumCounters(ctx, [
                      DetailType.CREATED,
                      DetailType.PRICING_CHANGES,
                      DetailType.NON_RELEVANT_CHANGES,
                      DetailType.NON_MODIFIED,
                      DetailType.DISABLED,
                  ])
                : sumCounters(ctx, [DetailType.CREATED_MODIFIED, DetailType.NON_MODIFIED]),
    },
    CatImport: { id: 'CatImport', ...CAT_IMPORT_PRESENTATION },
    'cat-import': { id: 'cat-import', ...CAT_IMPORT_PRESENTATION },
    ObjectSchemaImportJob: {
        id: 'ObjectSchemaImportJob',
        category: 'import',
        // Counts come from stats (schemas created/updated/deleted), not from node cards.
        sections: DEFAULT_SECTIONS,
    },
    'cgt-import-sap-hierarchy': {
        id: 'cgt-import-sap-hierarchy',
        category: 'import',
        sections: DEFAULT_SECTIONS,
        counters: () => [
            { detailType: DetailType.CREATED, counterType: CounterType.SUCCESS },
            { detailType: DetailType.NEW_FAMILIES_ADDED, counterType: CounterType.UPDATED },
            { detailType: DetailType.REALIGNED_FAMILIES, counterType: CounterType.UPDATED },
        ],
    },
    // The MM Dispo values come from SAP, never from a file. Every run, dry runs included, attaches
    // an xlsx recap of the nodes created, renamed, deleted and kept because still linked.
    'cgt-import-sap-mm-dispo': {
        id: 'cgt-import-sap-mm-dispo',
        category: 'import',
        sections: DEFAULT_SECTIONS,
        counters: () => [
            { detailType: DetailType.CREATED, counterType: CounterType.SUCCESS },
            { detailType: DetailType.CREATED_MODIFIED, counterType: CounterType.UPDATED },
        ],
    },
    // The monthly spare parts price list read from the CAT share: no file is attached to the run.
    // The unchanged parts are only counted (most of the ~90k parts on a monthly run): no drill-down.
    'cgt-import-ricambi': {
        id: 'cgt-import-ricambi',
        category: 'import',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.CREATED, counterType: CounterType.SUCCESS },
            { detailType: DetailType.CREATED_MODIFIED, counterType: CounterType.UPDATED },
            {
                detailType: DetailType.NON_MODIFIED,
                counterType: CounterType.INFO,
                disableDetail: true,
            },
        ],
    },
    configitImport: { id: 'configitImport', category: 'import', sections: DEFAULT_SECTIONS },
    importBom: { id: 'importBom', category: 'import', sections: DEFAULT_SECTIONS },
    'boomi-importer': { id: 'boomi-importer', category: 'import', sections: DEFAULT_SECTIONS },
    // Users come from Keycloak, never from a file: no attachment, and the cards count the user
    // nodes the run touched.
    'import-idp-users': {
        id: 'import-idp-users',
        category: 'import',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.CREATED, counterType: CounterType.SUCCESS },
            { detailType: DetailType.CREATED_MODIFIED, counterType: CounterType.SUCCESS },
            { detailType: DetailType.NON_MODIFIED, counterType: CounterType.INFO },
            { detailType: DetailType.DISABLED, counterType: CounterType.WARNING },
        ],
        recordCount: ctx =>
            sumCounters(ctx, [
                DetailType.CREATED_MODIFIED,
                DetailType.NON_MODIFIED,
                DetailType.DISABLED,
            ]),
    },

    // ---- Export ----------------------------------------------------------------
    ExportXlsx: { id: 'ExportXlsx', category: 'export', sections: DEFAULT_SECTIONS },
    ExportHistory: { id: 'ExportHistory', category: 'export', sections: DEFAULT_SECTIONS },
    'cpq-list-export': { id: 'cpq-list-export', category: 'export', sections: DEFAULT_SECTIONS },
    ObjectSchemaExportJob: {
        id: 'ObjectSchemaExportJob',
        category: 'export',
        sections: DEFAULT_SECTIONS,
    },
    exportCommercialItems: {
        id: 'exportCommercialItems',
        category: 'export',
        sections: DEFAULT_SECTIONS,
    },
    exportCommonData: { id: 'exportCommonData', category: 'export', sections: DEFAULT_SECTIONS },
    exportGls: { id: 'exportGls', category: 'export', sections: DEFAULT_SECTIONS },
    'export-trasversal-gls': {
        id: 'export-trasversal-gls',
        category: 'export',
        sections: DEFAULT_SECTIONS,
    },

    // ---- Hierarchy operations --------------------------------------------------
    AlignPrices: {
        id: 'AlignPrices',
        category: 'hierarchy',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.UPDATED_MODELS, counterType: CounterType.WARNING },
            { detailType: DetailType.UPDATED_GLS, counterType: CounterType.WARNING },
        ],
    },
    'publish-family': {
        id: 'publish-family',
        category: 'hierarchy',
        sections: DEFAULT_SECTIONS,
        counters: () => [{ detailType: DetailType.CREATED, counterType: CounterType.SUCCESS }],
    },
    'close-family': {
        id: 'close-family',
        category: 'hierarchy',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.REMOVED_SCHEDE, counterType: CounterType.SUCCESS },
        ],
    },
    'reopen-family': {
        id: 'reopen-family',
        category: 'hierarchy',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.REPUBLISHED_SCHEDE, counterType: CounterType.SUCCESS },
        ],
    },
    // Rewrites permissions on existing nodes only: no counters beyond the error card.
    'propagate-node-permissions': {
        id: 'propagate-node-permissions',
        category: 'hierarchy',
        sections: NO_FILES,
    },
    'duplicate-node': {
        id: 'duplicate-node',
        category: 'hierarchy',
        sections: NO_FILES,
        counters: () => [{ detailType: DetailType.CREATED, counterType: CounterType.SUCCESS }],
    },
    // Bulk change: the scan only builds the preview (no node written); the apply, and a rule run
    // that applied on its own, report the nodes they modified and the ones they left (conflicts,
    // values a rule skipped).
    'find-replace-scan': { id: 'find-replace-scan', category: 'hierarchy', sections: NO_FILES },
    'find-replace-apply': {
        id: 'find-replace-apply',
        category: 'hierarchy',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.CREATED_MODIFIED, counterType: CounterType.SUCCESS },
            { detailType: DetailType.NON_MODIFIED, counterType: CounterType.INFO },
        ],
        recordCount: ctx =>
            sumCounters(ctx, [DetailType.CREATED_MODIFIED, DetailType.NON_MODIFIED]),
    },
    'bulk-change-rule': {
        id: 'bulk-change-rule',
        category: 'hierarchy',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.CREATED_MODIFIED, counterType: CounterType.SUCCESS },
            { detailType: DetailType.NON_MODIFIED, counterType: CounterType.INFO },
        ],
        recordCount: ctx =>
            sumCounters(ctx, [DetailType.CREATED_MODIFIED, DetailType.NON_MODIFIED]),
    },
    'restart-translation': {
        id: 'restart-translation',
        category: 'hierarchy',
        sections: NO_FILES,
        counters: () => [
            { detailType: DetailType.CREATED_MODIFIED, counterType: CounterType.SUCCESS },
        ],
    },
    'publish-hierarchy': { id: 'publish-hierarchy', category: 'hierarchy', sections: NO_FILES },
    'duplicate-hierarchy': {
        id: 'duplicate-hierarchy',
        category: 'hierarchy',
        sections: NO_FILES,
    },
    'auto-translate-hierarchy': {
        id: 'auto-translate-hierarchy',
        category: 'hierarchy',
        sections: NO_FILES,
    },
    Publish: { id: 'Publish', category: 'hierarchy', sections: NO_FILES },
    // The push writes to an external service, not to PIM: no counter cards, no output file.
    pushBomPackage: { id: 'pushBomPackage', category: 'hierarchy', sections: NO_FILES },

    // ---- Data validation & quality --------------------------------------------
    massiveCheck: { id: 'massiveCheck', category: 'quality', sections: DEFAULT_SECTIONS },
    coolsalesMisalignedOffers: {
        id: 'coolsalesMisalignedOffers',
        category: 'quality',
        sections: DEFAULT_SECTIONS,
    },
    'zeus-coolsales-diff': {
        id: 'zeus-coolsales-diff',
        category: 'quality',
        sections: DEFAULT_SECTIONS,
    },

    // ---- Maintenance & configuration ------------------------------------------
    'cleanup-eventstore': {
        id: 'cleanup-eventstore',
        category: 'maintenance',
        sections: NO_FILES,
    },
    'cleanup-external-call-log': {
        id: 'cleanup-external-call-log',
        category: 'maintenance',
        sections: NO_FILES,
    },
    'cleanup-user-analytics': {
        id: 'cleanup-user-analytics',
        category: 'maintenance',
        sections: NO_FILES,
    },
    'generate-snapshot': {
        id: 'generate-snapshot',
        category: 'maintenance',
        sections: NO_FILES,
    },
    'expire-price-lists': {
        id: 'expire-price-lists',
        category: 'maintenance',
        sections: NO_FILES,
    },
    pboDataMigration: { id: 'pboDataMigration', category: 'maintenance', sections: NO_FILES },
    migrateDecisionTableCells: {
        id: 'migrateDecisionTableCells',
        category: 'maintenance',
        sections: NO_FILES,
    },
};

/**
 * Resolves the presentation for a job type. Keyed on the raw string rather than the JobResultView
 * union, because the server has roughly twice as many job types as the union declares — and
 * an old document may carry a type that no longer exists at all.
 */
export const getJobPresentation = (type: string): JobPresentation =>
    JOB_PRESENTATIONS[type] ?? { id: type, sections: DEFAULT_SECTIONS };
